/**
 * Partnr edit decorations (ours, MIT): the signal-wash highlight that fades
 * over 1.2s on every range Partnr just changed, the Review layer that shows
 * insertions (wash) and deletions (struck through, ink-3) between a snapshot
 * and the current document, and the attached selection: the text the user
 * handed Partnr, kept marked while focus is in the chat and through the
 * turn. Decorations only: the document itself never carries them, so
 * nothing of this is ever saved.
 */
import { Extension, type Editor } from '@tiptap/core';
import { Plugin, PluginKey, type EditorState } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import type { Node as PMNode } from '@tiptap/pm/model';
import { buildTextIndex, positionAt, rangeForText } from '@/lib/doc/textIndex';
import type { DiffHunk } from '@/lib/doc/diff';

export const HIGHLIGHT_MS = 1200;

interface PartnrEditsState {
  highlights: DecorationSet;
  review: DecorationSet;
  attached: DecorationSet;
}

type Meta =
  | { type: 'highlight'; from: number; to: number; id: number }
  | { type: 'unhighlight'; id: number }
  | { type: 'review'; hunks: DiffHunk[] | null }
  | { type: 'attach'; from: number; to: number }
  | { type: 'detach' };

export const partnrEditsKey = new PluginKey<PartnrEditsState>('partnrEdits');

/** A review position the previous and next buttons step through. */
export interface ReviewStop {
  pos: number;
}

/** Decorations for review hunks, positioned in `doc` (the current document). */
export function reviewDecorations(doc: PMNode, hunks: DiffHunk[]): Decoration[] {
  const index = buildTextIndex(doc);
  const out: Decoration[] = [];
  hunks.forEach((hunk, i) => {
    let anchor: number | null = null;
    if (hunk.newEnd > hunk.newStart) {
      const range = rangeForText(index, hunk.newStart, hunk.newEnd);
      if (range && range.to > range.from) {
        out.push(Decoration.inline(range.from, range.to, { class: 'partnr-diff-ins', 'data-review-stop': String(i) }));
        anchor = range.from;
      }
    }
    if (hunk.deleted.trim()) {
      // Deleted text sits where it was, before any insertion that replaced it
      const pos = anchor ?? positionAt(index, hunk.newStart);
      if (pos != null) {
        out.push(
          Decoration.widget(
            pos,
            () => {
              const el = document.createElement('span');
              el.className = 'partnr-diff-del';
              el.setAttribute('data-review-stop', String(i));
              el.setAttribute('contenteditable', 'false');
              el.textContent = hunk.deleted.replace(/\s+/g, ' ');
              return el;
            },
            { side: -1, key: `del-${i}-${hunk.deleted.length}`, ignoreSelection: true },
          ),
        );
      }
    }
  });
  return out;
}

export const PartnrEdits = Extension.create({
  name: 'partnrEdits',
  addProseMirrorPlugins() {
    return [
      new Plugin<PartnrEditsState>({
        key: partnrEditsKey,
        state: {
          init: () => ({ highlights: DecorationSet.empty, review: DecorationSet.empty, attached: DecorationSet.empty }),
          apply(tr, value) {
            let { highlights, review, attached } = value;
            highlights = highlights.map(tr.mapping, tr.doc);
            review = review.map(tr.mapping, tr.doc);
            // The attached text follows edits around it
            attached = attached.map(tr.mapping, tr.doc);
            const meta = tr.getMeta(partnrEditsKey) as Meta | undefined;
            if (meta?.type === 'highlight' && meta.to > meta.from) {
              highlights = highlights.add(tr.doc, [
                Decoration.inline(meta.from, meta.to, { class: 'partnr-edit' }, { id: meta.id }),
              ]);
            } else if (meta?.type === 'unhighlight') {
              highlights = highlights.remove(highlights.find(undefined, undefined, (spec) => spec.id === meta.id));
            } else if (meta?.type === 'review') {
              review = meta.hunks ? DecorationSet.create(tr.doc, reviewDecorations(tr.doc, meta.hunks)) : DecorationSet.empty;
            } else if (meta?.type === 'attach') {
              const to = Math.min(meta.to, tr.doc.content.size);
              attached = to > meta.from ? DecorationSet.create(tr.doc, [Decoration.inline(meta.from, to, { class: 'partnr-attached' })]) : DecorationSet.empty;
            } else if (meta?.type === 'detach') {
              attached = DecorationSet.empty;
            }
            return { highlights, review, attached };
          },
        },
        props: {
          decorations(state: EditorState) {
            const s = partnrEditsKey.getState(state);
            if (!s) return null;
            if (s.review === DecorationSet.empty && s.attached === DecorationSet.empty) return s.highlights;
            return DecorationSet.create(state.doc, [...s.attached.find(), ...s.highlights.find(), ...s.review.find()]);
          },
        },
      }),
    ];
  },
});

let nextHighlightId = 0;

/** Washes a range in signal and fades it out over 1.2s. */
export function highlightRange(editor: Editor, from: number, to: number): void {
  if (editor.isDestroyed || to <= from) return;
  const id = ++nextHighlightId;
  editor.view.dispatch(editor.state.tr.setMeta(partnrEditsKey, { type: 'highlight', from, to, id }).setMeta('addToHistory', false));
  // The CSS animation does the fade; this removes the decoration after it
  setTimeout(() => {
    if (editor.isDestroyed) return;
    editor.view.dispatch(editor.state.tr.setMeta(partnrEditsKey, { type: 'unhighlight', id }).setMeta('addToHistory', false));
  }, HIGHLIGHT_MS + 200);
}

/** Marks the text attached to the chat (it stays marked when focus leaves the page), or clears it with null. */
export function setAttachedRange(editor: Editor, range: { from: number; to: number } | null): void {
  if (editor.isDestroyed) return;
  const meta: Meta = range ? { type: 'attach', from: range.from, to: range.to } : { type: 'detach' };
  editor.view.dispatch(editor.state.tr.setMeta(partnrEditsKey, meta).setMeta('addToHistory', false));
}

/** Shows review hunks, or clears them with null. */
export function setReview(editor: Editor, hunks: DiffHunk[] | null): void {
  if (editor.isDestroyed) return;
  editor.view.dispatch(editor.state.tr.setMeta(partnrEditsKey, { type: 'review', hunks }).setMeta('addToHistory', false));
}
